<template>
  <section class="h-full flex flex-col text-sm">
    <!-- Expanded: PanelMenu with visual hierarchy -->
    <section v-if="drawerVisible" class="overflow-y-auto overflow-x-hidden flex-1 px-1 pt-1">
      <PanelMenu
        :model="menuItems"
        class="!border-none !min-w-0"
        multiple
        v-model:expandedKeys="expandedKeys"
      >
        <template #item="{ item }">
          <section
            class="flex items-center cursor-pointer select-none rounded-md"
            :class="[
              item.isChild
                ? 'py-1.5 pl-10 pr-3 text-slate-600'
                : 'py-2 px-[15px] font-medium',
              { 'bg-slate-100': isCurrentRoute(item) }
            ]"
          >
            <span
              v-if="item.icon"
              class="material-symbols-rounded"
              :class="item.isChild ? 'text-[18px]' : 'text-[22px]'"
            >{{ item.icon }}</span>
            <span class="text-nowrap pl-3">{{ item.label }}</span>
            <span class="flex-1" />
            <span
              v-if="item.items"
              class="material-symbols-rounded text-sm transition-transform duration-200"
              :class="{ 'rotate-180': isExpanded(item) }"
            >
              arrow_drop_down
            </span>
          </section>
        </template>
      </PanelMenu>
    </section>

    <!-- Collapsed: icon-only with popover flyouts -->
    <section v-else class="overflow-y-auto overflow-x-hidden flex-1 pt-1 px-1 flex flex-col items-center gap-0.5">
      <template v-for="item in menuItems" :key="item.key">
        <section
          v-tooltip.right="{ value: item.label, pt: { text: 'text-sm' } }"
          class="flex items-center justify-center w-full py-2.5 cursor-pointer rounded-md hover:bg-slate-100 relative"
          :class="{ 'bg-slate-100': isCurrentRoute(item) }"
          @click="handleCollapsedClick($event, item, popoverRef)"
        >
          <span class="material-symbols-rounded text-[22px]">{{ item.icon }}</span>
          <span
            v-if="item.items"
            class="absolute right-1 top-1 w-1 h-1 rounded-full bg-slate-400"
          />
        </section>
      </template>

      <Popover ref="popoverRef" class="collapsed-nav-popover">
        <ul class="list-none p-0 m-0 flex flex-col min-w-44">
          <li
            v-for="subItem in activePopoverItems"
            :key="subItem.key"
            class="flex items-center gap-2.5 px-3 py-2 hover:bg-slate-100 cursor-pointer rounded-md text-slate-700"
            :class="{ '!bg-slate-100 font-medium': isActiveSubItem(subItem) }"
            @click="handleSubItemClick(subItem, popoverRef)"
          >
            <span class="material-symbols-rounded text-[18px]">{{ subItem.icon }}</span>
            <span class="text-sm">{{ subItem.label }}</span>
          </li>
        </ul>
      </Popover>
    </section>

  </section>
</template>

<script setup lang="ts">
import Popover from 'primevue/popover';
import {PanelMenu} from "primevue";
import {ref} from "vue";
import {useDrawerMenu} from "@/composables/useDrawerMenu";

const popoverRef = ref();

const {
  expandedKeys,
  activePopoverItems,
  drawerVisible,
  menuItems,
  isCurrentRoute,
  isExpanded,
  isActiveSubItem,
  handleCollapsedClick,
  handleSubItemClick,
} = useDrawerMenu();
</script>

<style scoped>
:deep(.p-menu-list) {
  padding: 0 !important;
}

:deep(.p-menu-item-link) {
  padding-top: 3px !important;
  padding-bottom: 3px !important;
}

:deep(.p-panelmenu-submenu) {
  padding-left: 0 !important;
}

:deep(.p-panelmenu-submenu > * + *) {
  margin-top: 2px;
}

:deep(.p-menu-submenu-label) {
  padding-top: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

:deep(.p-menu-item-content) {
  border-radius: 7px !important;
}

:deep(.p-panelmenu-panel) {
  border: none !important;
  padding: 0 !important;
}

:deep(.p-panelmenu) {
  gap: 0 !important;
}
</style>